import { useState, useMemo } from 'react';
import { AlertTriangle, AlertOctagon, Info } from 'lucide-react';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { cn } from '../lib/cn';
import type { AppNotification, NotificationSeverity } from '../mocks/clientBatch';
import { usePendingModalBroadcasts } from '../api/platform';

const SEVERITY_META: Record<
  NotificationSeverity,
  { Icon: typeof Info; tone: string; bg: string }
> = {
  info: {
    Icon: Info,
    tone: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-50 dark:bg-sky-950/30',
  },
  warning: {
    Icon: AlertTriangle,
    tone: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
  },
  critical: {
    Icon: AlertOctagon,
    tone: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-950/30',
  },
};

/**
 * Pops up the FIRST pending modal-banner broadcast after login.
 *
 * State is in-component: closing the modal hides it for the rest of the
 * user-session (no persistence). "Don't show this again" is recorded as a
 * dismissed-id so reopening this component for the same notification is a
 * no-op (mock — not actually persisted across reloads).
 */
export function PostLoginModalBanner() {
  const { data: modalBroadcasts = [] } = usePendingModalBroadcasts();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());
  const [closedForSession, setClosedForSession] = useState<Set<string>>(() => new Set());
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const pending = useMemo(
    () => modalBroadcasts.filter((n) => !dismissedIds.has(n.id) && !closedForSession.has(n.id)),
    [modalBroadcasts, dismissedIds, closedForSession],
  );

  const current: AppNotification | undefined = pending[0];

  if (!current) return null;

  const meta = SEVERITY_META[current.severity];
  const { Icon, tone, bg } = meta;

  function close() {
    if (!current) return;
    if (dontShowAgain) {
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.add(current.id);
        return next;
      });
    } else {
      setClosedForSession((prev) => {
        const next = new Set(prev);
        next.add(current.id);
        return next;
      });
    }
    setDontShowAgain(false);
  }

  return (
    <Modal
      open={true}
      onClose={close}
      size="md"
      footer={
        <Button onClick={close} fullWidth={false}>
          Got it
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <span className={cn('w-10 h-10 rounded-md grid place-items-center flex-shrink-0', bg)}>
            <Icon className={cn('w-5 h-5', tone)} />
          </span>
          <div className="flex-1 min-w-0 pt-1">
            <h2 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">{current.title}</h2>
          </div>
        </div>

        <p className="text-sm text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap leading-relaxed">
          {current.body}
        </p>

        {current.actionUrl && (
          <a
            href={current.actionUrl}
            target={current.actionUrl.startsWith('http') ? '_blank' : undefined}
            rel="noreferrer"
            className="text-sm font-medium text-brand dark:text-brand-dm hover:underline inline-flex items-center gap-1"
          >
            {current.actionLabel ?? 'Learn more'} →
          </a>
        )}

        <label className="flex items-center gap-2 pt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="text-brand focus:ring-brand"
          />
          <span className="text-sm text-zinc-700 dark:text-zinc-200">Don't show this again</span>
        </label>
      </div>
    </Modal>
  );
}
